import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { Country } from '../../types/countryTypes';
import { getAllCountries, deleteCountry } from '../../api/countryApi';
import CountryForm from '../../components/CountryForm';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useCountry } from '../../contexts/CountryContext';
import { getMyCountries } from '../../api/countryApi';

/**
 * Pantalla de administración de países (Fase 4). Permite crear, editar y dar de baja países, y
 * definir su configuración por defecto (moneda, jornada, decimales, horas extra, subcontratas, roster).
 */
const Countries: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Country | null>(null);
  const { showMessage } = useSnackbar();
  const { setCountries: refreshUserCountries } = useCountry();

  const fetchCountries = async () => {
    try {
      setCountries(await getAllCountries());
    } catch (e) {
      showMessage('Error al cargar los países.', 'error');
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleEdit = (country: Country) => {
    setEditing(country);
    setFormOpen(true);
  };

  const handleDeactivate = async (country: Country) => {
    if (!window.confirm(`¿Dar de baja el país "${country.name}"?`)) return;
    try {
      await deleteCountry(country.id);
      showMessage('País dado de baja.', 'success');
      await fetchCountries();
      // Refresca el selector del Navbar (el país inactivo debe desaparecer).
      try {
        refreshUserCountries(await getMyCountries());
      } catch {
        /* no-op */
      }
    } catch (e) {
      showMessage('Error al dar de baja el país.', 'error');
    }
  };

  const handleSaved = async () => {
    showMessage('País guardado correctamente.', 'success');
    await fetchCountries();
    // Refresca la lista de países del usuario (para que el selector del Navbar se actualice).
    try {
      refreshUserCountries(await getMyCountries());
    } catch {
      /* no-op */
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4">Administración de Países</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nuevo país
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Aquí se define la configuración <strong>por defecto</strong> de cada país (moneda, jornada, etc.).
        Cada proyecto la hereda y puede sobrescribir lo permitido desde su pestaña "País y configuración".
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Nombre</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="right"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {countries.map((country) => (
              <TableRow key={country.id} hover>
                <TableCell>{country.code}</TableCell>
                <TableCell>{country.name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={country.status !== false ? 'Activo' : 'Inactivo'}
                    color={country.status !== false ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar país y configuración">
                    <IconButton onClick={() => handleEdit(country)}><EditIcon /></IconButton>
                  </Tooltip>
                  {country.status !== false && (
                    <Tooltip title="Dar de baja">
                      <IconButton color="error" onClick={() => handleDeactivate(country)}><BlockIcon /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {countries.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">No hay países registrados.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CountryForm
        open={formOpen}
        country={editing}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default Countries;
